import React, { useState } from 'react';
import { useProject } from '../../../contexts/ProjectContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMapMarkerAlt, faInfoCircle, faChartBar, faCheckCircle, faBuilding } from '@fortawesome/free-solid-svg-icons';
import WhatsAppModal from '../../../components/WhatsAppModal';
import brandConfig from '../../../config/brandConfig';

// Icono de WhatsApp - usar emoji si el paquete de brands no está disponible
let WhatsAppIcon = null;
try {
  const { faWhatsapp } = require('@fortawesome/free-brands-svg-icons');
  WhatsAppIcon = faWhatsapp;
} catch (e) {
  // Si no está disponible, usaremos un emoji
  console.warn('@fortawesome/free-brands-svg-icons no está instalado, usando emoji para WhatsApp');
}

const AboutProjectModal = ({ isOpen, onClose }) => {
  const { project } = useProject();
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null); // { type: 'agent' | 'admin', data: {...} }

  if (!isOpen) return null;

  const services = [
    { key: 'has_water', label: 'Agua', icon: '💧' },
    { key: 'has_electricity', label: 'Electricidad', icon: '⚡' },
    { key: 'has_sewage', label: 'Desagüe', icon: '🚰' },
    { key: 'has_gas', label: 'Gas Natural', icon: '🔥' },
    { key: 'has_internet', label: 'Internet', icon: '📶' },
    { key: 'has_phone', label: 'Teléfono', icon: '📞' }
  ];

  const availableServices = services.filter(s => project?.[s.key]);

  // Función para abrir el modal de WhatsApp con formulario
  const openWhatsAppForm = (contactData, type = 'agent') => {
    setSelectedContact({ type, data: contactData });
    setWhatsAppModalOpen(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-theme-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Compacto */}
        <div className="bg-gradient-to-r from-brand-50 to-brand-100 border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faInfoCircle} className="text-white text-xs" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 truncate">{project?.name || 'Información del proyecto'}</h2>
                {project?.location && (
                  <div className="flex items-center space-x-1 text-[10px] text-gray-600">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-brand-600 text-[9px]" />
                    <span className="truncate">{project.location}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-md hover:bg-white/70 flex items-center justify-center transition-colors text-gray-600 hover:text-gray-900 flex-shrink-0 ml-2"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-3 space-y-2.5">
          {/* Descripción */}
          {(project?.description || project?.total_area || project?.currency) && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Descripción</h4>
              <div className="text-xs text-gray-700 leading-relaxed space-y-1.5">
                {project?.description && (
                  <p className="leading-snug">{project.description}</p>
                )}
                {(project?.total_area || project?.currency) && (
                  <div className="pt-1.5 border-t border-gray-200 text-[10px] text-gray-600">
                    {project?.total_area && (
                      <span>Área Total del proyecto: <strong className="text-gray-900">{project.total_area.toLocaleString()} m²</strong></span>
                    )}
                    {project?.total_area && project?.currency && <span className="mx-1.5">•</span>}
                    {project?.currency && (
                      <span>Moneda: <strong className="text-gray-900">{project.currency}</strong></span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Estadísticas de Lotes - Compacto */}
          {project?.lots_statistics && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center space-x-1.5">
                <FontAwesomeIcon icon={faChartBar} className="text-brand-600 text-[10px]" />
                <span>Estadísticas de Lotes</span>
              </h4>
              <div className="grid grid-cols-4 gap-1.5">
                <div className="text-center p-1.5 bg-success-50 rounded border border-success-200">
                  <div className="text-base font-bold text-success-700">{project.lots_statistics.available || 0}</div>
                  <div className="text-[9px] font-medium text-success-600">Disponibles</div>
                </div>
                <div className="text-center p-1.5 bg-warning-50 rounded border border-warning-200">
                  <div className="text-base font-bold text-warning-700">{project.lots_statistics.reserved || 0}</div>
                  <div className="text-[9px] font-medium text-warning-600">Reservados</div>
                </div>
                <div className="text-center p-1.5 bg-error-50 rounded border border-error-200">
                  <div className="text-base font-bold text-error-700">{project.lots_statistics.sold || 0}</div>
                  <div className="text-[9px] font-medium text-error-600">Vendidos</div>
                </div>
                <div className="text-center p-1.5 bg-brand-50 rounded border border-brand-200">
                  <div className="text-base font-bold text-brand-700">{project.lots_statistics.total || 0}</div>
                  <div className="text-[9px] font-medium text-brand-600">Total</div>
                </div>
              </div>
            </div>
          )}

          {/* Servicios - Compacto en texto */}
          {availableServices.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center space-x-1.5">
                <FontAwesomeIcon icon={faCheckCircle} className="text-brand-600 text-[10px]" />
                <span>Servicios Disponibles</span>
              </h4>
              <div className="text-xs text-gray-700 leading-relaxed">
                {availableServices.map((service, index) => (
                  <span key={service.key}>
                    {index > 0 && <span className="mx-1.5 text-gray-400">•</span>}
                    <span>{service.icon} {service.label}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Organización - Compacto */}
          {project?.organization && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center space-x-1.5">
                <FontAwesomeIcon icon={faBuilding} className="text-brand-600 text-[10px]" />
                <span>Organización</span>
              </h4>
              <div className="flex items-center space-x-2">
                {project.organization.logo_url ? (
                  <img
                    src={project.organization.logo_url}
                    alt={project.organization.name}
                    className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center border border-gray-200 flex-shrink-0">
                    <FontAwesomeIcon icon={faBuilding} className="text-brand-600 text-sm" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">{project.organization.name}</p>
                  {project.organization.description && (
                    <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-1">{project.organization.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Contacto por WhatsApp - Agentes y Admin */}
          {((project?.assigned_agents?.length > 0) || (project?.assigned_agents?.length === 0 && project?.admin_contact)) && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center space-x-1.5">
                {WhatsAppIcon ? (
                  <FontAwesomeIcon icon={WhatsAppIcon} className="text-success-600 text-[10px]" />
                ) : (
                  <span className="text-success-600 text-[10px]">💬</span>
                )}
                <span>Contactar Agente inmobiliario por WhatsApp</span>
              </h4>
              <div className="space-y-1.5">
                {/* Agentes asignados - Solo mostrar si hay agentes */}
                {project?.assigned_agents?.length > 0 && project?.assigned_agents.map((agent) => (
                  <div key={agent.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                      {agent.profile_picture ? (
                        <img
                          src={agent.profile_picture}
                          alt={agent.full_name || 'Agente'}
                          className="w-7 h-7 rounded-full object-cover border border-gray-200 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center border border-gray-200 flex-shrink-0">
                          <span className="text-[10px] font-semibold text-brand-600">
                            {(agent.full_name || agent.email || 'A')[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-gray-900 truncate">{agent.full_name || 'Agente'}</div>
                        <div className="text-[9px] text-gray-500 truncate">Agente inmobiliaria</div>
                      </div>
                    </div>
                    {agent.phone_number && (
                      <button
                        onClick={() => openWhatsAppForm(agent, 'agent')}
                        className="ml-1.5 px-2.5 py-1 bg-success-500 text-white rounded-lg hover:bg-success-600 transition-colors text-[10px] font-semibold flex items-center space-x-1 whitespace-nowrap"
                      >
                        {WhatsAppIcon ? (
                          <FontAwesomeIcon icon={WhatsAppIcon} className="text-xs" />
                        ) : (
                          <span className="text-xs">💬</span>
                        )}
                        <span>Contactar</span>
                      </button>
                    )}
                  </div>
                ))}

                {/* Admin - Solo mostrar si NO hay agentes */}
                {(!project?.assigned_agents || project?.assigned_agents.length === 0) && project?.admin_contact && (
                  <div className="flex items-center justify-between p-2 bg-brand-50 rounded-lg border border-brand-200 hover:bg-brand-100 transition-colors">
                    <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                      {project.admin_contact.profile_picture ? (
                        <img
                          src={project.admin_contact.profile_picture}
                          alt={project.admin_contact.full_name || 'Administrador'}
                          className="w-7 h-7 rounded-full object-cover border border-brand-200 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-brand-200 flex items-center justify-center border border-brand-300 flex-shrink-0">
                          <span className="text-[10px] font-semibold text-brand-700">
                            {(project.admin_contact.full_name || 'A')[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-gray-900 truncate">
                          {project.admin_contact.full_name || 'Administrador'}
                        </div>
                        <div className="text-[9px] text-gray-500 truncate">Administrador</div>
                      </div>
                    </div>
                    {(project.admin_contact.phone_number || project.admin_contact.organization_whatsapp) && (
                      <button
                        onClick={() => openWhatsAppForm(project.admin_contact, 'admin')}
                        className="ml-1.5 px-2.5 py-1 bg-success-500 text-white rounded-lg hover:bg-success-600 transition-colors text-[10px] font-semibold flex items-center space-x-1 whitespace-nowrap"
                      >
                        {WhatsAppIcon ? (
                          <FontAwesomeIcon icon={WhatsAppIcon} className="text-xs" />
                        ) : (
                          <span className="text-xs">💬</span>
                        )}
                        <span>Contactar</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Información de Contacto - Email, Teléfono y Web */}
          {(project?.admin_contact?.organization_email || project?.organization?.email || project?.organization?.phone || project?.organization?.website) && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-2 uppercase tracking-wide">Información de Contacto</h4>
              
              {/* Email */}
              {(project?.admin_contact?.organization_email || project?.organization?.email) && (
                <div className="flex items-center space-x-2 text-xs text-gray-700">
                  <span className="text-gray-600">Correo:</span>
                  <a 
                    href={`mailto:${project?.admin_contact?.organization_email || project?.organization?.email}`} 
                    className="text-brand-600 hover:text-brand-700 hover:underline font-medium"
                  >
                    {project?.admin_contact?.organization_email || project?.organization?.email}
                  </a>
                </div>
              )}

              {/* Teléfono */}
              {(project?.organization?.phone || project?.admin_contact?.organization_phone) && (
                <div className="flex items-center space-x-2 text-xs text-gray-700">
                  <span className="text-gray-600">Teléfono:</span>
                  <a 
                    href={`tel:${project?.organization?.phone || project?.admin_contact?.organization_phone}`} 
                    className="text-brand-600 hover:text-brand-700 hover:underline font-medium"
                  >
                    {project?.organization?.phone || project?.admin_contact?.organization_phone}
                  </a>
                </div>
              )}

              {/* Sitio Web */}
              {project?.organization?.website && (
                <div className="flex items-center space-x-2 text-xs text-gray-700">
                  <span className="text-gray-600">Sitio Web:</span>
                  <a 
                    href={project.organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:text-brand-700 hover:underline font-medium truncate"
                  >
                    {project.organization.website}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Amenidades - Si existen */}
          {project?.amenities && project.amenities.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="text-[10px] font-semibold text-gray-700 mb-2 uppercase tracking-wide">Amenidades</h4>
              <div className="flex flex-wrap gap-1.5">
                {project.amenities.map((amenity, index) => (
                  <span key={index} className="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-700">
                    {amenity}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer - Compacto */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-2">
          <button
            onClick={onClose}
            className="w-full px-3 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-xs font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* WhatsApp Modal con Formulario */}
      {whatsAppModalOpen && selectedContact && (
        <WhatsAppModal
          isOpen={whatsAppModalOpen}
          onClose={() => {
            setWhatsAppModalOpen(false);
            setSelectedContact(null);
          }}
          onSubmit={(formData) => {
            // Obtener el número de WhatsApp del contacto seleccionado
            const phoneNumber = selectedContact.type === 'agent'
              ? selectedContact.data.phone_number
              : (selectedContact.data.organization_whatsapp || selectedContact.data.phone_number);
            
            if (!phoneNumber) return;

            // Limpiar el número de teléfono
            const cleanPhone = phoneNumber.replace(/[^0-9+]/g, '');
            const whatsappNumber = cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`;
            
            // Generar mensaje personalizado
            const contactName = selectedContact.data.full_name || 'Equipo';
            const message = `Hola ${contactName}, me interesa obtener más información sobre los lotes del proyecto ${project?.name || ''}.\n\n` +
              `📱 Mi contacto: ${formData.phone || '___'}\n` +
              `👤 Nombre: ${formData.firstName || '___'}\n` +
              (formData.email ? `📧 Email: ${formData.email}\n` : '') +
              `\n¿Podrían enviarme más información sobre los lotes disponibles?`;
            
            // Remove + from whatsappNumber for wa.me URL
            const whatsappNumberClean = whatsappNumber.replace('+', '');
            const whatsappUrl = `https://wa.me/${whatsappNumberClean}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
          }}
          project={project}
          lotProperties={null} // No hay lote específico desde el modal del proyecto
          lotId={null}
          projectId={project?.id}
          organizationPhone={selectedContact?.data?.organization_whatsapp || selectedContact?.data?.phone_number}
          organizationId={project?.organization_id}
          apiUrl={brandConfig.apiUrl}
        />
      )}
    </div>
  );
};

export default AboutProjectModal;
